import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { downloadAdminFile } from "../../app/files";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminUiStore } from "../../app/providers";
import { pathForExportJob } from "../../app/routes";
import { saveBlob } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import { ExportView } from "./components/ExportView";
import { useExportDetail, useExportList, useRequestExport } from "./queries";
import {
  defaultExportRequest,
  parseExportDate,
  parseExportJobId,
  parseExportPage,
  parseExportStatus,
  parseExportType,
} from "./schema";
import type { ExportListCriteria, ExportRequestValues } from "./schema";

const PAGE_SIZE = 20;

function paramsFor(criteria: ExportListCriteria, jobId?: string) {
  const params = new URLSearchParams();
  if (criteria.status) params.set("status", criteria.status);
  if (criteria.exportType) params.set("exportType", criteria.exportType);
  if (criteria.requestedFrom) params.set("requestedFrom", criteria.requestedFrom);
  if (criteria.requestedTo) params.set("requestedTo", criteria.requestedTo);
  if (criteria.page) params.set("page", String(criteria.page));
  if (jobId) params.set("jobId", jobId);
  return params;
}

function detailState(query: ReturnType<typeof useExportDetail>, jobId?: string) {
  if (!jobId) return "idle" as const;
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

export function ExportPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeNavigate = useNavigate();
  const services = useAdminApplication();
  const rememberListContext = useAdminUiStore((state) => state.rememberListContext);
  const search = searchParams.toString();
  const criteria = useMemo<ExportListCriteria>(() => {
    const params = new URLSearchParams(search);
    return {
      status: parseExportStatus(params.get("status")),
      exportType: parseExportType(params.get("exportType")),
      requestedFrom: parseExportDate(params.get("requestedFrom")),
      requestedTo: parseExportDate(params.get("requestedTo")),
      page: parseExportPage(params.get("page")),
      size: PAGE_SIZE,
    };
  }, [search]);
  const jobId = parseExportJobId(searchParams.get("jobId"));
  const list = useExportList(criteria);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [pollGeneration, setPollGeneration] = useState(0);
  const detail = useExportDetail(jobId, !pollTimedOut);
  const request = useRequestExport();
  const [notice, setNotice] = useState("");
  const fileRequests = useRef(new Set<AbortController>());
  const navigation = useAdminNavigation({
    contextKey: "exports",
    defaultListPath: "/operations/exports?page=0",
    originLabel: "정산 Export 목록",
  });
  const restorationNotice = useRestoreAdminListViewport("exports", Boolean(list.data));

  useEffect(() => {
    if (jobId) {
      routeNavigate(pathForExportJob(jobId), { replace: true });
      return;
    }
    const canonical = paramsFor(criteria, jobId);
    if (canonical.toString() !== search) setSearchParams(canonical, { replace: true });
  }, [criteria, jobId, routeNavigate, search, setSearchParams]);

  useEffect(() => {
    if (!list.data || list.data.items.length > 0 || list.data.totalCount === 0 || criteria.page === 0) return;
    const lastPage = Math.max(0, Math.ceil(list.data.totalCount / criteria.size) - 1);
    const nextCriteria = { ...criteria, page: lastPage };
    const params = paramsFor(nextCriteria, jobId);
    rememberListContext("exports", {
      path: `${location.pathname}${params.size ? `?${params.toString()}` : ""}`,
      page: lastPage,
      filters: {
        status: criteria.status,
        exportType: criteria.exportType,
        requestedFrom: criteria.requestedFrom,
        requestedTo: criteria.requestedTo,
        size: String(criteria.size),
      },
    }, true);
    setSearchParams(params, { replace: true });
  }, [criteria, jobId, list.data, location.pathname, rememberListContext, setSearchParams]);

  useEffect(() => {
    setPollTimedOut(false);
    if (!jobId) return;
    const timer = window.setTimeout(() => setPollTimedOut(true), services.polling.timeoutMs);
    return () => window.clearTimeout(timer);
  }, [jobId, pollGeneration, services.polling.timeoutMs]);

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const retryDetail = () => {
    setPollGeneration((value) => value + 1);
    setPollTimedOut(false);
    void detail.refetch();
  };

  const submitRequest = async (values: ExportRequestValues) => {
    const job = await request.mutateAsync(values);
    setNotice(`${job.jobId} 작업을 ${job.status} 상태로 접수했습니다. 파일 준비 여부를 확인 중입니다.`);
    setPollGeneration((value) => value + 1);
    navigation.openDetail(pathForExportJob(job.jobId));
    return job;
  };

  const download = useCallback(async (job: Schemas["DataExportJobView"]) => {
    if (job.status !== "READY" || !job.fileAssetId) throw new Error("The export file is not ready.");
    const controller = new AbortController();
    fileRequests.current.add(controller);
    try {
      const blob = await downloadAdminFile(services, job.fileAssetId, {}, controller.signal);
      saveBlob(blob, `carepass-settlements-${job.from}-${job.to}.csv`);
      setNotice("");
    } finally {
      fileRequests.current.delete(controller);
    }
  }, [services]);

  return <ExportView
    mode="list"
    criteria={criteria}
    result={list.data}
    listState={list.isPending ? "loading" : list.isError ? "error" : "ready"}
    selectedJobId={jobId}
    selected={detail.data}
    detailState={detailState(detail, jobId)}
    pollTimedOut={pollTimedOut && ["REQUESTED", "PROCESSING"].includes(detail.data?.status ?? "")}
    defaultRequest={defaultExportRequest()}
    notice={[notice, restorationNotice].filter(Boolean).join(" ")}
    onSearch={(next) => { setNotice(""); setSearchParams(paramsFor({ ...next, page: 0 })); navigation.focusListResults(); }}
    onPageChange={(page) => { setSearchParams(paramsFor({ ...criteria, page })); navigation.focusListResults(); }}
    onSelect={(next) => navigation.openDetail(pathForExportJob(next), `export:${next}:detail`)}
    detailHref={pathForExportJob}
    onOpenDetail={(next) => navigation.openDetail(pathForExportJob(next), `export:${next}:detail`)}
    onClearSelection={() => setSearchParams(paramsFor(criteria))}
    onRetryList={async () => {
      const result = await list.refetch();
      if (result.error) throw result.error;
    }}
    onRetryDetail={retryDetail}
    onRequest={submitRequest}
    onDownload={download}
  />;
}
